<template>
  <div
    ref="containerRef"
    class="tabs"
    :class="[`tabs--${tabsPlacement}`, { 'tabs--dragging': isDragging }]"
    @mousedown="onDragStart"
    @click.capture="onClickCapture"
  >
    <slot>
      <BasicTab
        v-for="tab in tabs"
        :tabKey="tab.tabKey"
        :tabState="tab.tabState"
        :key="tab.tabKey!"
      />
    </slot>
  </div>
</template>

<script setup lang="ts">
  import { provide, ref, nextTick, onBeforeUnmount, watch } from 'vue'
  import { BasicTabsKey, type BasicTabsProvided, type TabsModel } from './BasicTabs.types'

  const selectedTab = defineModel<TabsModel>()

  defineProps<{
    tabs?: TabProps[]
    tabsPlacement?: 'center' | 'start'
  }>()

  const containerRef = ref<HTMLElement | null>(null)
  const isDragging = ref(false)
  const DRAG_THRESHOLD = 5
  let startX = 0
  let startScrollLeft = 0
  let dragDistance = 0

  const onDragStart = (e: MouseEvent) => {
    if (!containerRef.value || e.button !== 0) return
    startX = e.pageX
    startScrollLeft = containerRef.value.scrollLeft
    dragDistance = 0
    document.addEventListener('mousemove', onDragMove)
    document.addEventListener('mouseup', onDragEnd)
  }

  const onDragMove = (e: MouseEvent) => {
    if (!containerRef.value) return
    const walk = e.pageX - startX
    if (!isDragging.value && Math.abs(walk) > DRAG_THRESHOLD) {
      isDragging.value = true
    }
    if (isDragging.value) {
      e.preventDefault()
      containerRef.value.scrollLeft = startScrollLeft - walk
      dragDistance = Math.abs(walk)
    }
  }

  const onDragEnd = () => {
    document.removeEventListener('mousemove', onDragMove)
    document.removeEventListener('mouseup', onDragEnd)
    requestAnimationFrame(() => {
      isDragging.value = false
    })
  }

  const onClickCapture = (e: MouseEvent) => {
    if (dragDistance > DRAG_THRESHOLD) {
      e.stopPropagation()
      e.preventDefault()
      dragDistance = 0
    }
  }

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onDragMove)
    document.removeEventListener('mouseup', onDragEnd)
  })

  const scrollSelectedTabIntoView = (tabKey: TabsModel) => {
    nextTick(() => {
      const container = containerRef.value
      if (!container) return
      const escaped = CSS.escape(String(tabKey))
      const target = container.querySelector<HTMLElement>(`[data-tab-key="${escaped}"]`)
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      }
    })
  }

  watch(selectedTab, (value) => scrollSelectedTabIntoView(value))

  const change = (value: TabsModel) => {
    selectedTab.value = value
  }

  provide<BasicTabsProvided>(BasicTabsKey, {
    selectedTab,
    change,
  })
</script>

<style lang="less">
  .dark {
    .tabs {
      background-color: #012133;
    }
  }

  .tabs {
    background-color: @neutral-0;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: @white transparent;

    &--dragging {
      user-select: none;
    }

    &--center {
      justify-content: space-between;
    }

    &::-webkit-scrollbar {
      height: 2px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: @white;
      border-radius: 2px;
    }

    .tab .text {
      display: inline;
      overflow: visible;
      text-overflow: clip;
      -webkit-line-clamp: unset;
      line-clamp: unset;
      word-break: normal;
      white-space: nowrap;
    }

    .tab:nth-child(1):nth-last-child(-n+4),
    .tab:nth-child(1):nth-last-child(-n+4) ~ .tab {
      min-width: 200px;
    }
  }
</style>
